import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { FeedbackModal as ErrorModal } from "../../../components/ui/modals/FeedbackModal";
import { useNavigate } from "react-router-dom";
import {
  SelectVertical,
  loadGameCategory,
  gameProvider,
  loadGameData,
  gameLogin,
  loadGameDataCategoryWise,
  updateFavouriteGameStatus,
  loadGameDirectly,
  loadSupplier,
  checkScheduleMaintenance,
} from "../services/casino.service";
import { GameListResponse } from "../models/responses/gameListResponse";
import { GameDataList } from "../models/responses/gameListResponse";
import { GameCategoryListResponse } from "../models/responses/GameCategoryListResponse";
import { GameCategoryDataList } from "../models/responses/GameCategoryListResponse";
import { GameProviderResponse } from "../models/responses/GameProviderResponse";
import { BlobContainerConsts } from "../../../models/constants/blobContainerConsts";
import { DraggableCarousel } from "../../../components/ui/carousel";
import { GameSupplierList } from "../models/responses/supplierListResponse";
import InfiniteScroll from "../../../components/ui/InfiniteScroll";
import { useLanguage } from "../../../config/localization/LanguageProvider";
import localization from "../../../config/localization";
import { BottomDrawer } from "../../../components/ui/BottomDrawer";
import { GameSupplier } from "./GameSupplier";
import { Button } from "../../../components/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faStar, faXmark } from "@fortawesome/free-solid-svg-icons";
import { localStorageService } from "../../../services";
import { BLOB_IMAGE_URL } from "../../../config/appConfig";
import { changeLoaderState } from "../../../reducers/appStateReducer";
import { useAppDispatch } from "../../../store/hooks";
import userDeviceStore from "../../../store/device.store";
const PAGE_SIZE = 20;

const supplierNameToSlug = (name: string) => name.replace(/ /g, "-");

const findSupplierFromSlug = (
  slug: string,
  suppliers: GameSupplierList[],
): string | null => {
  return (
    suppliers.find((s) => s.name.replace(/ /g, "-") === slug)?.name ?? null
  );
};

export function Casino() {
  const blobUrl = process.env.REACT_APP_BLOB_IMAGE_URL as string;

  const { state } = useLocation();
  const accessModeSource = userDeviceStore((state) => state.accessModeSource);

  const { "*": gamePath } = useParams();
  const { language } = useLanguage();
  const getCategoryImageUrl = (
    categoryImagePath: string | null | undefined,
  ): string => {
    if (categoryImagePath) {
      return `${blobUrl}${BlobContainerConsts.GameCategory}/${categoryImagePath}`;
    }
    return "/images/icon-upload.png";
  };

  const getGameImageUrl = (webImagePath: string | null | undefined): string => {
    let imageUrl = "/images/ImageNotFound.jpg";
    const trimmedPath = webImagePath?.trim();

    if (webImagePath?.trim()) {
      imageUrl =
        trimmedPath?.includes("https://") || trimmedPath?.includes("http://")
          ? trimmedPath
          : `${blobUrl}${BlobContainerConsts.GameBanner}/${webImagePath}`;
    }

    imageUrl = imageUrl.replace(/'/g, "%27");

    return imageUrl;
  };

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // Provider state
  const [providerNames, setProviderNames] =
    useState<GameProviderResponse | null>(null);

  // Category state
  const [gameCategoryListResponse, setGameCategoryListResponse] =
    useState<GameCategoryListResponse | null>(null);
  const [allCategoryId, setAllCategoryId] = useState<string | undefined>();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    state?.selectedCategoryId ?? null,
  );

  useEffect(() => {
    if (state?.selectedCategoryId) {
      setSelectedCategoryId(state.selectedCategoryId);
    }
  }, [state?.selectedCategoryId]);

  // Game data states
  const [gameListResponse, setGameListResponse] = useState<GameListResponse>({
    gameList: [],
  });
  const [pageNumber, setPageNumber] = useState(0);
  // State for ALL view - games grouped by category
  const [allCategoriesGameData, setAllCategoriesGameData] = useState<
    {
      categoryId: string;
      categoryName: string;
      categoryHindiName: string;
      categoryImagePath: string;
      games: GameDataList[];
    }[]
  >([]);

  const [gameSupplierList, setGameSupplierList] = useState<GameSupplierList[]>(
    [],
  );
  const [gameCategoryId, setGameCategoryId] = useState<string | null>(null);
  const [casinoError, setCasinoError] = useState<string>("");

  const [searchText, setSearchText] = useState<string>("");

  // Infinite scroll states
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const [isSupplierDrawerOpen, setIsSupplierDrawerOpen] = useState(false);
  const isGameClickExecuting = useRef(false);
  const [isGameClickDisabled, setIsGameClickDisabled] = useState(false);
  const [selectedSupplierName, setSelectedSupplierName] = useState<
    string | null
  >(null);

  // Initialize on mount
  useEffect(() => {
    init();
  }, []);

  // Load games when All is selected and categories are ready
  useEffect(() => {
    if (
      selectedCategoryId === allCategoryId &&
      allCategoryId &&
      gameCategoryListResponse &&
      !isLoading &&
      !selectedSupplierName &&
      allCategoriesGameData.length === 0 &&
      !searchText.trim()
    ) {
      loadAllCategoryGames();
    }
  }, [
    selectedCategoryId,
    allCategoryId,
    gameCategoryListResponse,
    isLoading,
    allCategoriesGameData.length,
    selectedSupplierName,
    searchText,
  ]);

  useEffect(() => {
    if (searchText.trim()) {
      setGameListResponse({ gameList: [] });
    }

    const timer = setTimeout(() => {
      if (searchText.trim()) {
        resetPagination();
        setAllCategoriesGameData([]);
        const categoryToSearch =
          selectedCategoryId === "favourite"
            ? "favourite"
            : selectedCategoryId === allCategoryId
              ? null
              : selectedCategoryId;
        fetchData(true, searchText, categoryToSearch);
      } else {
        resetPagination();
        if (selectedCategoryId === "favourite") {
          fetchData(true, null, "favourite");
        } else if (selectedCategoryId) {
          fetchData(true, null, selectedCategoryId);
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText]);

  async function init() {
    setIsLoading(true);
    dispatch(changeLoaderState(true));
    if (gamePath && gamePath.split("/").length > 1) {
      const launchUrl = await loadGameDirectly(gamePath, {
        setCasinoError: (message) => setCasinoError(message),
      }, accessModeSource.sourceType);
      if (launchUrl) {
        dispatch(changeLoaderState(false));
        navigate("/gamelaunch", { state: { launchUrl } });
        return;
      }
    } else {
      await SelectVertical();

      const isMaintenance = await checkScheduleMaintenance();
      if (isMaintenance) {
        setIsLoading(false);
        dispatch(changeLoaderState(false));
        return;
      }
      await gameProvider(setProviderNames);

      const {
        gameCategoryListResponse: categoryResponse,
        allCategoryId: apiAllCategoryId,
      } = await loadGameCategory();

      if (categoryResponse) {
        let categories = [...(categoryResponse.gameCategoryList || [])];

        // Sort: Move "All" category from API to first position
        categories.sort((a, b) =>
          a.name.toLowerCase() === "all"
            ? -1
            : b.name.toLowerCase() === "all"
              ? 1
              : 0,
        );
        const isLoggedIn =
          localStorageService.getItem("isUserLoggedIn") === true;
        if (isLoggedIn) {
          // Create "Favourite" category
          const favouriteCategory: GameCategoryDataList = {
            id: "favourite",
            name: "Favourite",
            isDefault: false,
            categoryImagePath: "",
            sequence: 1,
            blobImageUrl: "",
            hindiName: "पसंदीदा",
          };

          // Insert Favourite at position 1 (after All)
          categories.splice(1, 0, favouriteCategory);
        }

        setGameCategoryListResponse({
          ...categoryResponse,
          gameCategoryList: categories,
        });

        setAllCategoryId(apiAllCategoryId);

        // Default select "All" category
        if (state?.selectedCategoryId || apiAllCategoryId) {
          setSelectedCategoryId(state?.selectedCategoryId ?? apiAllCategoryId ?? null);
        }
      }
      const suppliers = await loadSupplier(null);
      setGameSupplierList(suppliers);

      if (gamePath && !gamePath.includes("/")) {
        const matchedSupplier = findSupplierFromSlug(gamePath, suppliers);
        if (matchedSupplier) {
          setSelectedSupplierName(matchedSupplier);
          const { gameListResponse: res } = await loadGameData(null, 0, null, {
            gameCategoryId: undefined,
            gameSupplierName: matchedSupplier,
            isFavouriteGames: false,
            pageSize: PAGE_SIZE,
          });
          setGameListResponse(res);
          setPageNumber(1);
          if (res.gameList.length < PAGE_SIZE) setHasMore(false);
        }
      }
    }
    setIsLoading(false);
    dispatch(changeLoaderState(false));
  }

  // Load 20 games for each category (for ALL view)
  const loadAllCategoryGames = useCallback(async () => {
    if (!gameCategoryListResponse?.gameCategoryList || !allCategoryId) return;

    const categoriesToLoad = gameCategoryListResponse.gameCategoryList.filter(
      (cat) => cat.id !== allCategoryId && cat.id !== "favourite",
    );

    const results = await Promise.all(
      categoriesToLoad.map(async (category) => {
        const { gameListResponse: res } = await loadGameData(null, 0, null, {
          gameCategoryId: category.id,
          gameSupplierName: undefined,
          isFavouriteGames: false,
          pageSize: PAGE_SIZE,
        });

        return {
          categoryId: category.id,
          categoryName: category.name,
          categoryHindiName: category.hindiName,
          categoryImagePath: category.categoryImagePath,
          games: res.gameList,
        };
      }),
    );

    const categoriesWithGames = results.filter((r) => r.games.length > 0);
    setAllCategoriesGameData(categoriesWithGames);
  }, [gameCategoryListResponse, allCategoryId]);

  // Reset pagination state
  const resetPagination = () => {
    setGameListResponse({ gameList: [] });
    setPageNumber(0);
    setHasMore(true);
    setIsLoadingMore(false);
  };

  // Unified fetch for single-category / favourite / search (Deposit pattern)
  const fetchData = useCallback(
    async (
      isReset = false,
      overrideSearch?: string | null,
      overrideCategoryId?: string | null,
      overrideSupplierName?: string | null | undefined,
    ) => {
      if (isLoadingMore && !isReset) return;

      const effectiveCategoryId =
        overrideCategoryId !== undefined
          ? overrideCategoryId
          : selectedCategoryId;

      const effectiveSupplierName =
        overrideSupplierName !== undefined
          ? overrideSupplierName
          : selectedSupplierName;
      if (effectiveCategoryId === allCategoryId && !effectiveSupplierName)
        return;

      setIsLoadingMore(true);

      const currentPageNo = isReset ? 0 : pageNumber;
      const search = overrideSearch !== undefined ? overrideSearch : searchText;

      const { gameListResponse: res } = await loadGameData(
        search,
        currentPageNo,
        isReset ? null : gameListResponse.gameList,
        {
          gameCategoryId:
            effectiveCategoryId === "favourite" ||
              (effectiveCategoryId === allCategoryId && !effectiveSupplierName)
              ? undefined
              : (effectiveCategoryId ?? undefined),
          gameSupplierName: effectiveSupplierName ?? undefined,
          isFavouriteGames: effectiveCategoryId === "favourite",
          pageSize: PAGE_SIZE,
        },
      );

      setGameListResponse(res);

      // If fewer than PAGE_SIZE returned, no more pages
      const newGamesCount = isReset
        ? res.gameList.length
        : res.gameList.length - gameListResponse.gameList.length;

      if (newGamesCount < PAGE_SIZE) {
        setHasMore(false);
      }

      setPageNumber((prev) => (isReset ? 1 : prev + 1));
      setIsLoadingMore(false);
    },
    [
      isLoadingMore,
      selectedCategoryId,
      allCategoryId,
      pageNumber,
      searchText,
      gameListResponse.gameList,
      selectedSupplierName,
    ],
  );

  // Helper function for loadGameDataCategoryWise
  const handleLoadGameDataCategoryWise = useCallback(
    async (categoryId: string | null, isFavourite: boolean) => {
      await loadGameDataCategoryWise(categoryId, isFavourite, {
        setGameSupplierList,
        setGameCategoryId,
        setPageNumber,
        setGameListResponse,
        loadGameData: async () => {
          const effectiveId = isFavourite ? "favourite" : categoryId;
          await fetchData(true, undefined, effectiveId);
        },
        searchParam: searchText ?? "",
        setCasinoError: (message) => {
          setCasinoError(message);
        },
      });
    },
    [searchText, fetchData],
  );

  // Handle category click
  const handleCategoryClick = useCallback(
    async (categoryId: string) => {
      setSelectedCategoryId(categoryId);
      resetPagination();
      setAllCategoriesGameData([]);

      if (categoryId === allCategoryId) {
        const suppliers = await loadSupplier(null);
        setGameSupplierList(suppliers);
        if (selectedSupplierName) {
          await fetchData(
            true,
            searchText,
            allCategoryId,
            selectedSupplierName,
          );
        } else {
          await loadAllCategoryGames();
        }
      } else if (categoryId === "favourite") {
        await handleLoadGameDataCategoryWise(null, true);
      } else {
        await handleLoadGameDataCategoryWise(categoryId, false);
      }
    },
    [
      allCategoryId,
      loadAllCategoryGames,
      handleLoadGameDataCategoryWise,
      selectedSupplierName,
      fetchData,
      searchText,
    ],
  );

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  /* Game Login */
  const handleGameClick = useCallback(
    async (game: GameDataList) => {

      // Lock: prevent double-clicks (same pattern as C# isExecuting lock)
      if (isGameClickExecuting.current) return;
      dispatch(changeLoaderState(true));
      isGameClickExecuting.current = true;
      setIsGameClickDisabled(true);

      try {
        const baseUri = window.location.origin;

        const gameUrl = game.gameURL.includes("?")
          ? `${game.gameURL}&home=${encodeURIComponent(baseUri)}&exit_url=${encodeURIComponent(baseUri)}`
          : `${game.gameURL}?home=${encodeURIComponent(baseUri)}&exit_url=${encodeURIComponent(baseUri)}`;
        const launchUrl = await gameLogin(
          gameUrl,
          game.providerName,
          game.code,
          {
            gameId: game.id,
            gameSupplierId: game.gameSupplierId,
            gameSupplierName: game.supplierName,
            searchParam: searchText ?? "",
            setCasinoError: (message) => {
              setCasinoError(message);
            },
          },
          accessModeSource.sourceType,
        );

        if (launchUrl) {
          navigate("/gamelaunch", { state: { launchUrl } });
        }
      } finally {
        await new Promise((resolve) => setTimeout(resolve, 1300));
        isGameClickExecuting.current = false;
        setIsGameClickDisabled(false);
        dispatch(changeLoaderState(false));
      }
    },
    [searchText],
  );
  const handleFavouriteToggle = useCallback(
    async (game: GameDataList) => {
      await updateFavouriteGameStatus(game.id, !game.isFavourite, {
        setPageNumber,
        setGameListResponse,
        loadGameData: async () => {
          if (selectedCategoryId === allCategoryId) {
            await loadAllCategoryGames();
          } else {
            resetPagination();
            await fetchData(true);
          }
        },
      });
    },
    [selectedCategoryId, allCategoryId, loadAllCategoryGames, fetchData],
  );

  return (
    <div className="casino-container">
      <>
        {/* Category Tabs */}
        <div className="flex items-center gap-4">
          {/* Mobile: search input (replaces categories in same row) */}
          {isSearchOpen && (
            <div className="flex items-center gap-2 flex-1 min-w-0 md:hidden">
              <div className="relative flex-1 min-w-0">
                <input
                  type="text"
                  value={searchText}
                  onChange={handleSearchInputChange}
                  placeholder={localization.searchgame}
                  autoFocus
                  className="bg-gray-800 text-white pl-4 pr-10 py-2 rounded-lg border border-gray-700 focus:border-primary focus:outline-none w-full"
                />
                <img
                  src="/icons/ic-casino-search.svg"
                  alt="Search"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 opacity-60"
                />
              </div>
              <Button
                onClick={() => setIsSupplierDrawerOpen(true)}
                className={`flex-shrink-0 flex items-center justify-center gap-1 px-3 py-2 rounded-lg font-medium ${selectedSupplierName
                  ? "bg-white text-black"
                  : "bg-[#232323] text-[#A9A9A9]"
                  }`}
              >
                <img
                  src={
                    selectedSupplierName
                      ? "/icons/ic-filter-black.svg"
                      : "/icons/ic-filter-white.svg"
                  }
                  alt="Filter Icon"
                />
                <span>Filter</span>
              </Button>
            </div>
          )}

          {/* Category carousel (hidden on mobile when search is open) */}
          <div
            className={`flex-1 overflow-hidden ${isSearchOpen ? "hidden md:block" : ""}`}
          >
            <DraggableCarousel>
              {gameCategoryListResponse?.gameCategoryList?.map((category) => (
                <Button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 whitespace-nowrap border-b-2 ${selectedCategoryId === category.id
                    ? "border-white text-white"
                    : "border-transparent text-gray-400"
                    }`}
                >
                  <div className="w-5 h-5">
                    <img
                      src={
                        category.id === allCategoryId
                          ? "/icons/ic-casino-all.svg"
                          : category.id === "favourite"
                            ? "/icons/ic-casino-favourite-games.svg"
                            : getCategoryImageUrl(category.categoryImagePath)
                      }
                      alt={category.name}
                    ></img>
                  </div>
                  <span>
                    {language === "hi"
                      ? category.hindiName || category.name
                      : category.name}
                  </span>
                </Button>
              ))}
            </DraggableCarousel>
          </div>

          {/* Desktop: search input */}
          <div className="relative flex-shrink-0 hidden md:block">
            <input
              type="text"
              value={searchText}
              onChange={handleSearchInputChange}
              placeholder={localization.searchgame}
              className="bg-gray-800 text-white pl-4 pr-10 py-2 rounded-lg border border-gray-700 focus:border-primary focus:outline-none w-48"
            />
            <img
              src="/icons/ic-casino-search.svg"
              alt="Search"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 w-6 h-6 opacity-60"
            />
          </div>

          {/* Desktop: filter Button */}
          <Button
            onClick={() => setIsSupplierDrawerOpen(true)}
            className={`hidden md:flex items-center justify-center gap-1 px-3 py-2 rounded-lg font-medium ${selectedSupplierName
              ? "bg-white text-black"
              : "bg-[#232323] text-[#A9A9A9]"
              }`}
          >
            <img
              src={
                selectedSupplierName
                  ? "/icons/ic-filter-black.svg"
                  : "/icons/ic-filter-white.svg"
              }
              alt="Filter Icon"
            />
            <span>Filter</span>
          </Button>

          {/* Mobile: magnifier / close toggle */}
          {/* Mobile: magnifier / close toggle */}
          <Button
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            className="flex-shrink-0 md:hidden p-2 rounded-full bg-gray-800"
          >
            {isSearchOpen ? (
              <FontAwesomeIcon
                icon={faXmark}
                className="w-6 h-6 text-white"
                size="sm"
              />
            ) : (
              <img
                src="/icons/ic-casino-search.svg"
                alt="Search"
                className="w-6 h-6"
              />
            )}
          </Button>
        </div>
        {/* Games Display */}
        <div className="games-container mt-4">
          {selectedCategoryId === allCategoryId &&
            !selectedSupplierName &&
            !searchText.trim() ? (
            // ALL view - show each category with its games
            allCategoriesGameData.length > 0 ? (
              allCategoriesGameData.map((categoryData) => (
                <div
                  key={categoryData.categoryId}
                  className="text-white bg-dark-gradient rounded-2xl mt-6 p-4 mb-6"
                >
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-3">
                      <img
                        src={`${BLOB_IMAGE_URL}${BlobContainerConsts.GameCategory}/${categoryData.categoryImagePath}`}
                        alt={categoryData.categoryName}
                        className="h-6 w-6 object-contain"
                      />
                      <span className="text-lg md:text-xl font-semibold" >
                        {language === "hi" ? categoryData.categoryHindiName : categoryData.categoryName}
                      </span>
                    </div>

                    <Button
                      type="button"
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0d0f19] text-white shadow-md hover:scale-105 transition-transform"
                      onClick={() =>
                        handleCategoryClick(categoryData.categoryId)
                      }
                    >
                      <FontAwesomeIcon icon={faArrowRight} className="text-sm" />
                    </Button>
                  </div>
                  <DraggableCarousel gap="gap-8">
                    {categoryData.games.map((game) => (
                      <div
                        key={game.id}
                        className={`game-card group relative w-[99px] md:w-[165px] flex-shrink-0 overflow-hidden ${isGameClickDisabled ? "cursor-default" : "cursor-pointer"}`}
                        onClick={() => handleGameClick(game)}
                      >
                        <div className="absolute inset-0 bg-black/40 rounded-lg transition-opacity opacity-0 group-hover:opacity-100" />

                        <img
                          src={getGameImageUrl(game.webImagePath)}
                          alt={game.name}
                          className="w-[99px] h-[130px] md:w-[165px] md:h-[218px] rounded-lg object-cover"
                        />
                        <span
                          className="text-white text-sm mt-1 block truncate"
                          title={game.name}
                        >
                          {game.name}
                        </span>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFavouriteToggle(game);
                          }}
                          className={`absolute top-2 right-2 p-1 transition-opacity ${game.isFavourite
                            ? "opacity-100"
                            : "opacity-0 group-hover:opacity-100"
                            }`}
                        >
                          {game.isFavourite ? (
                            <FontAwesomeIcon
                              icon={faStar}
                              className="w-5 h-5 text-[#FFD700]"
                            />
                          ) : (
                            <FontAwesomeIcon
                              icon={faStar}
                              className="w-5 h-5"
                              style={{
                                color: "transparent",
                                stroke: "white",
                                strokeWidth: 20,
                              }}
                            />
                          )}
                        </Button>
                      </div>
                    ))}
                  </DraggableCarousel>
                </div>
              ))
            ) : (
              <div className="text-gray-400">No games found</div>
            )
          ) : // Single category view (Favourite or specific category)
            gameListResponse.gameList.length > 0 ? (
              <InfiniteScroll
                onLoadMore={() => fetchData()}
                hasMore={hasMore}
                isLoading={isLoadingMore}
              >
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4 md:gap-8">
                  {gameListResponse.gameList.map((game) => (
                    <div
                      key={game.id}
                      className={`game-card group relative w-full overflow-hidden ${isGameClickDisabled ? "cursor-default" : "cursor-pointer"}`}
                      onClick={() => handleGameClick(game)}
                    >
                      <div className="absolute inset-0 bg-black/40 rounded-lg transition-opacity opacity-0 group-hover:opacity-100" />

                      <img
                        src={getGameImageUrl(game.webImagePath)}
                        alt={game.name}
                        className="w-full aspect-[3/4] rounded-lg object-cover"
                      />
                      <span
                        className="text-white text-xs md:text-sm mt-1 block truncate"
                        title={game.name}
                      >
                        {game.name}
                      </span>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFavouriteToggle(game);
                        }}
                        className={`absolute top-2 right-2 p-1 transition-opacity ${game.isFavourite
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-100"
                          }`}
                      >
                        {game.isFavourite ? (
                          <FontAwesomeIcon
                            icon={faStar}
                            className="w-5 h-5 text-[#FFD700]"
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faStar}
                            className="w-5 h-5"
                            style={{
                              color: "transparent",
                              stroke: "white",
                              strokeWidth: 20,
                            }}
                          />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </InfiniteScroll>
            ) : isLoadingMore ? (
              <div className="text-gray-400">Loading games...</div>
            ) : (
              <div className="text-gray-400">No games found</div>
            )}
          <BottomDrawer
            open={isSupplierDrawerOpen}
            onClose={() => setIsSupplierDrawerOpen(false)}
            className="w-full lg:max-w-[80%] lg:mx-auto"
          >
            <GameSupplier
              suppliers={gameSupplierList}
              selectedSupplier={selectedSupplierName}
              onSelect={(name) => {
                setSelectedSupplierName(name);
                setIsSupplierDrawerOpen(false);

                // Update browser URL
                if (name) {
                  navigate(`/casino/${supplierNameToSlug(name)}`, {
                    replace: true,
                  });
                } else {
                  navigate("/casino", { replace: true });
                }

                resetPagination();
                setAllCategoriesGameData([]);

                if (name) {
                  fetchData(true, searchText, selectedCategoryId, name);
                } else if (selectedCategoryId === "favourite") {
                  fetchData(true, searchText, "favourite", null);
                } else {
                  fetchData(true, searchText, selectedCategoryId, null);
                }
              }}
            />
          </BottomDrawer>
        </div>
      </>
      {casinoError && (
        <ErrorModal
          id="casino-error-modal"
          isOpen={!!casinoError}
          title="Failed!"
          description={casinoError ?? ""}
          onClose={() => {
            setCasinoError("");
            navigate("/casino");
          }}
          blurColor="bg-[#FFD4D4]"
        />
      )}
    </div>
  );
}
